import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex, useOutsideClick } from '@chakra-ui/react';
import { Check, Close, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Divider } from 'uiToolkitV1/Divider';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import { StakeholderEmptyState } from './shared/stakeholderComponents';
import { StakeHolderTableProps } from './shared/stakeholderTypes';
import { formatCurrency } from './shared/stakeholderUtils';
import { setModifiedCurrentPayDetailsRows, useCompensationDrawersState } from './slice/compensationDrawersSlice';

/**
 * Get all descendant nodes (children) of a given node
 */
const getDescendants = (nodeId: string, allRows: any[]) => {
  const targetNode = allRows.find((row) => row.id === nodeId);
  if (!targetNode) return [];

  const descendants = [];
  const targetHierarchy = targetNode.hierarchy;

  allRows.forEach((row) => {
    // Check if this row is a descendant
    if (
      row.hierarchy.length > targetHierarchy.length &&
      row.hierarchy.slice(0, targetHierarchy.length).join(',') === targetHierarchy.join(',')
    ) {
      descendants.push(row);
    }
  });

  return descendants;
};

/**
 * Get all ancestor nodes (parents) of a given node
 */
const getAncestors = (nodeId: string, allRows: any[]) => {
  const targetNode = allRows.find((row) => row.id === nodeId);
  if (!targetNode) return [];

  const ancestors = [];
  const targetHierarchy = targetNode.hierarchy;

  // Get all parent levels
  for (let i = 1; i < targetHierarchy.length; i++) {
    const parentHierarchy = targetHierarchy.slice(0, i);
    const parentNode = allRows.find(
      (row) => row.hierarchy.length === parentHierarchy.length && row.hierarchy.join(',') === parentHierarchy.join(',')
    );
    if (parentNode) {
      ancestors.push(parentNode);
    }
  }

  return ancestors;
};

/**
 * Get immediate children of a node
 */
const getImmediateChildren = (nodeId: string, allRows: any[]) => {
  const targetNode = allRows.find((row) => row.id === nodeId);
  if (!targetNode) return [];

  const children = [];
  const targetHierarchy = targetNode.hierarchy;

  for (const row of allRows) {
    if (
      row.hierarchy.length === targetHierarchy.length + 1 &&
      row.hierarchy.slice(0, targetHierarchy.length).join(',') === targetHierarchy.join(',')
    ) {
      children.push(row);
    }
  }

  return children;
};

/**
 * Calculate aggregated values for parent nodes based on their children
 */
const calculateParentAggregates = (parentNode: any, children: any[]) => {
  if (children.length === 0) return parentNode;

  const totalCurrentPayroll = children.reduce((sum, child) => sum + Number(child.currentPayroll), 0);
  const totalIncreasedBudget = children.reduce((sum, child) => sum + Number(child.increasedBudget || 0), 0);
  const totalNewBudget = totalCurrentPayroll + totalIncreasedBudget;
  const averagePercentageIncrease = totalCurrentPayroll > 0 ? (totalIncreasedBudget / totalCurrentPayroll) * 100 : 0;

  return {
    ...parentNode,
    currentPayroll: totalCurrentPayroll,
    increasedBudget: totalIncreasedBudget,
    totalNewBudget,
    percentageIncrease: Number(averagePercentageIncrease.toFixed(2)),
  };
};

/**
 * Apply cascading updates throughout the hierarchy
 */
const applyCascadingUpdates = (
  updatedNodeId: string,
  newPercentageIncrease: number,
  newIncreasedBudget: number,
  allRows: any[],
  applyToAllLevels: boolean
) => {
  let updatedRows = [...allRows];

  // Update the current node
  updatedRows = updatedRows.map((row) => {
    if (row.id === updatedNodeId) {
      return {
        ...row,
        percentageIncrease: newPercentageIncrease,
        increasedBudget: newIncreasedBudget,
        totalNewBudget: Number(row.currentPayroll) + Number(newIncreasedBudget),
      };
    }
    return row;
  });

  if (applyToAllLevels) {
    // Cascade down to all descendants
    const descendants = getDescendants(updatedNodeId, updatedRows);

    updatedRows = updatedRows.map((row) => {
      if (descendants.find((desc) => desc.id === row.id)) {
        const newChildIncreasedBudget = Number((newPercentageIncrease * (Number(row.currentPayroll) / 100)).toFixed(2));
        return {
          ...row,
          percentageIncrease: newPercentageIncrease,
          increasedBudget: newChildIncreasedBudget,
          totalNewBudget: Number(row.currentPayroll) + newChildIncreasedBudget,
        };
      }
      return row;
    });
  }

  // Always cascade up to update parent aggregates
  const ancestors = getAncestors(updatedNodeId, updatedRows);

  // Update ancestors from bottom to top
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const ancestor = ancestors[i];
    const children = getImmediateChildren(ancestor.id, updatedRows);
    const updatedAncestor = calculateParentAggregates(ancestor, children);

    updatedRows = updatedRows.map((row) => {
      if (row.id === ancestor.id) {
        return updatedAncestor;
      }
      return row;
    });
  }

  return updatedRows;
};

// Custom hook to manage local state sync with Redux
const useLocalBudgetState = (data: any, modifiedRows: any[]) => {
  const [localPercentageIncrease, setLocalPercentageIncrease] = useState(data.percentageIncrease);
  const [localIncreasedBudget, setLocalIncreasedBudget] = useState(data.increasedBudget);
  const [isPercentageIncreaseFocused, setIsPercentageIncreaseFocused] = useState(false);
  const [isIncreasedBudgetFocused, setIsIncreasedBudgetFocused] = useState(false);
  const [lastSyncedData, setLastSyncedData] = useState({
    percentageIncrease: data.percentageIncrease,
    increasedBudget: data.increasedBudget,
  });

  useEffect(() => {
    const currentRowData = modifiedRows.find((row) => row.id === data.id);

    if (currentRowData && !isPercentageIncreaseFocused) {
      // Only update if the data has actually changed
      if (
        currentRowData.percentageIncrease !== lastSyncedData.percentageIncrease ||
        currentRowData.increasedBudget !== lastSyncedData.increasedBudget
      ) {
        setLocalPercentageIncrease(currentRowData.percentageIncrease);
        setLocalIncreasedBudget(currentRowData.increasedBudget);
        setLastSyncedData({
          percentageIncrease: currentRowData.percentageIncrease,
          increasedBudget: currentRowData.increasedBudget,
        });
      }
    }
  }, [modifiedRows, data.id, isPercentageIncreaseFocused, lastSyncedData]);

  return {
    localPercentageIncrease,
    setLocalPercentageIncrease,
    localIncreasedBudget,
    setLocalIncreasedBudget,
    isPercentageIncreaseFocused,
    setIsPercentageIncreaseFocused,
    isIncreasedBudgetFocused,
    setIsIncreasedBudgetFocused,
  };
};

// Restored PercentageIncreaseCell with original TWO inputs structure
const PercentageIncreaseCell = (params: any) => {
  const { data } = params;
  const dispatch = useDispatch();
  const { modifiedCurrentPayDetailsRows } = useCompensationDrawersState();
  const { percentageIncrease, totalNewBudget, currentPayroll, increasedBudget } = data;

  const {
    localPercentageIncrease,
    setLocalPercentageIncrease,
    localIncreasedBudget,
    setLocalIncreasedBudget,
    isPercentageIncreaseFocused,
    setIsPercentageIncreaseFocused,
    isIncreasedBudgetFocused,
    setIsIncreasedBudgetFocused,
  } = useLocalBudgetState(data, modifiedCurrentPayDetailsRows);

  const [isAllLevelsSelected, setIsAllLevelsSelected] = useState(true);

  const componentRef = useRef<HTMLDivElement>(null);

  const isFocused = isPercentageIncreaseFocused || isIncreasedBudgetFocused;

  // Update row data with focus state and refresh grid
  useEffect(() => {
    const currentRow = modifiedCurrentPayDetailsRows.find((row) => row.id === data.id);

    // Only update if focus state actually changed
    if (currentRow && (currentRow as any).isFocused !== isFocused) {
      const updatedRows = modifiedCurrentPayDetailsRows.map((row) => {
        if (row.id === data.id) {
          return {
            ...row,
            isFocused,
          };
        }
        return row;
      });
      dispatch(setModifiedCurrentPayDetailsRows(updatedRows));

      // Set height directly on the row node to avoid remounting components
      setTimeout(() => {
        if (params.api && params.node) {
          const newHeight = isFocused ? 115 : 66;
          params.node.setRowHeight(newHeight);
          params.api.onRowHeightChanged();
        }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id, dispatch, isFocused, params.api, params.node]);

  const handleBudgetUpdate = () => {
    const updatedRows = applyCascadingUpdates(
      data.id,
      Number(localPercentageIncrease),
      Number(localIncreasedBudget),
      modifiedCurrentPayDetailsRows,
      isAllLevelsSelected
    );
    dispatch(setModifiedCurrentPayDetailsRows(updatedRows));
    setIsPercentageIncreaseFocused(false);
    setIsIncreasedBudgetFocused(false);
  };

  const handleBudgetCancel = () => {
    setLocalPercentageIncrease(percentageIncrease);
    setLocalIncreasedBudget(increasedBudget);
    setIsPercentageIncreaseFocused(false);
    setIsIncreasedBudgetFocused(false);
  };

  // Handle outside click to cancel editing
  useOutsideClick({
    ref: componentRef,
    handler: () => {
      // Only handle outside click if component is actually focused
      if (isFocused) {
        handleBudgetCancel();
      }
    },
  });

  const appliesToOptions = [
    { value: 'all', label: 'all levels below' },
    { value: 'unchanged', label: 'only where unchanged' },
  ];
  const [appliesTo, setAppliesTo] = useState(isAllLevelsSelected ? appliesToOptions[0] : appliesToOptions[1]);

  return (
    <Box
      ref={componentRef}
      display="flex"
      height="100%"
      width="100%"
      padding="10px 12px"
      alignItems="center"
      gap="2px"
      border="0px solid #EBEFF3"
      background={isFocused ? '#FAFAFA' : 'white'}
    >
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap="2px" flexShrink="0">
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center">
            <InputV2
              value={isPercentageIncreaseFocused ? localPercentageIncrease : `${localPercentageIncrease}%`}
              isDashed
              width="70px"
              onChange={(e) => {
                // Remove % sign and keep only numbers
                const numericPercentageValue = e.target.value.replace('%', '').replace(/[^0-9.-]/g, '');
                setLocalPercentageIncrease(numericPercentageValue);
                // up to 2 decimal places
                const newIncreasedBudget = Number((Number(numericPercentageValue) * (currentPayroll / 100)).toFixed(2));
                setLocalIncreasedBudget(newIncreasedBudget);
              }}
              onFocus={() => setIsPercentageIncreaseFocused(true)}
              inputMode="decimal"
            />

            <Box borderRight="1px solid #CFD7DF" height="20px" margin="0 0" />
            <Box
              sx={{
                input: {
                  paddingLeft: '12px !important',
                },
              }}
            >
              <InputV2
                value={isIncreasedBudgetFocused ? localIncreasedBudget : formatCurrency(localIncreasedBudget)}
                isDashed
                width="105px"
                onChange={(e) => {
                  setLocalIncreasedBudget(e.target.value);
                  const newPercentageIncrease = Number(((Number(e.target.value) / currentPayroll) * 100).toFixed(2));
                  setLocalPercentageIncrease(newPercentageIncrease);
                }}
                inputMode="decimal"
                onFocus={() => setIsIncreasedBudgetFocused(true)}
              />
            </Box>
          </Box>
          {isFocused && (
            <Box display="flex" height="54px" padding="8px 0px" alignItems="center" gap="4px">
              <Check width="24px" height="24px" color="#0D819F" onClick={() => handleBudgetUpdate()} />
              <Close width="24px" height="24px" color="#C62C02" onClick={() => handleBudgetCancel()} />
            </Box>
          )}
        </Box>

        {isFocused && (
          <Box display="flex" alignItems="center" gap="8px" alignSelf="stretch">
            <Flex align="center" gap="1">
              <TextBodyMedium color="#666666" mr={1}>
                Applies to :
              </TextBodyMedium>
              <Select
                value={appliesTo.value}
                onValueChange={(value) => {
                  const selectedOption = appliesToOptions.find((option) => option.value === value);
                  if (selectedOption) setAppliesTo(selectedOption);
                  setIsAllLevelsSelected(selectedOption.value === 'all');
                }}
              >
                <SelectTrigger style={{ width: '173px' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ width: '200px' }}>
                  {appliesToOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const additionalColumns = [
  {
    columnId: 'currentPayroll',
    headerName: 'Current payroll',
    cellRenderer: (params: any) => <div>{formatCurrency(params.data.currentPayroll)}</div>,
  },
  {
    columnId: 'percentageIncrease',
    headerName: '% increased budget',
    cellRenderer: (params: any) => (
      <PercentageIncreaseCell
        {...params}
        key={`${params.data.id}-${params.data.percentageIncrease}-${params.data.increasedBudget}`}
      />
    ),
    width: 310,
  },
  {
    columnId: 'totalNewBudget',
    headerName: 'Total new budget',
    cellRenderer: (params: any) => <div>{formatCurrency(params.data.totalNewBudget)}</div>,
  },
];

const HierarchyLevelCell = ({ data }: { data: any }) => {
  const { function: functionData } = data;
  const { displayName, subtitle, isEmployee, isGroup, profilePhoto, designation, level, displayLevel } = functionData;

  if (isEmployee || level === 'EMPLOYEE') {
    return (
      <Box display="flex" alignItems="center" py={1}>
        <Avatar name={displayName} src={profilePhoto} size={32} mr={3} />
        <Box>
          <TextBodyMedium>{displayName}</TextBodyMedium>
          <TextLabel>{subtitle}</TextLabel>
          {designation && <TextLabelSmall>{designation}</TextLabelSmall>}
        </Box>
      </Box>
    );
  }

  if (isGroup) {
    return (
      <Box display="flex" flexDirection="column" padding="8px 12px">
        <TextLabelSmall>{displayLevel}</TextLabelSmall>
        <TextBodyMedium>{displayName}</TextBodyMedium>
        {subtitle && <TextLabel>{subtitle}</TextLabel>}
      </Box>
    );
  }

  // Fallback for any other cases
  return <TextBodyMedium>{displayName}</TextBodyMedium>;
};

function CurrentPayDetailsTreeTable({
  gridRef,
  gridStatePersistenceKey,
  columns = [],
  loading = false,
  error = null,
}: StakeHolderTableProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const { modifiedCurrentPayDetailsRows } = useCompensationDrawersState();
  const [columnDefs, setColumnDefs] = useState([]);

  // Set up column definitions
  useEffect(() => {
    const newColumnDefs = columns
      .map((col) => ({
        colId: col.columnId,
        field: col.columnId,
        id: col.columnId,
        displayName: col.displayName,
        description: col.description,
        headerName: col.displayName,
        minWidth: 220,
        ...getColumnDefinitionByType(col),
      }))
      .filter((col) => col.field === 'function');

    newColumnDefs.push(...additionalColumns);
    setColumnDefs(newColumnDefs);
  }, [columns]);

  // Use rowData from Redux if available, otherwise use prop data
  const displayData = modifiedCurrentPayDetailsRows;

  // Error state handling
  // if (error) {
  //   return (
  //     <StakeholderErrorState
  //       heading="No budget allocation details are available!"
  //       description="There was an issue fetching the budget allocation details. Please try again later."
  //     />
  //   );
  // }

  return (
    <Box
      display="flex"
      padding="8px 8px 12px 8px"
      flexDirection="column"
      alignItems="flex-start"
      gap="12px"
      alignSelf="stretch"
      borderRadius="8px"
      background="#FFF"
    >
      {displayData?.length === 0 && !loading ? (
        <StakeholderEmptyState heading="No budget allocation details are available!" />
      ) : (
        <>
          <Box width="100%">
            <Box
              display="flex"
              padding="8px 0px"
              alignItems="flex-start"
              gap="0px"
              alignSelf="stretch"
              borderRadius="0px"
            >
              <TextBodyMedium bold>Current pay details</TextBodyMedium>
            </Box>
            <Divider color="#EBEFF3" />
          </Box>

          <Box className="stakeholder-flat-list-table" width="100%" height="calc(100vh - 265px)">
            <AgGridTable
              key={gridStatePersistenceKey}
              gridStatePersistenceKey={gridStatePersistenceKey}
              ref={agGridRef}
              columnDefs={columnDefs}
              paginationAutoPageSize={false}
              headerHeight={40}
              // rowHeight={115}
              getRowHeight={(params) => (params.data.isFocused ? 115 : 66)}
              sideBar={{
                position: 'right',
                toolPanels: ['columns'],
                defaultToolPanel: undefined,
              }}
              loading={loading}
              rowData={displayData}
              getRowId={(params) => params.data.id}
              treeData
              animateRows
              groupDefaultExpanded={-1}
              getDataPath={(data) => data.hierarchy}
              autoGroupColumnDef={{
                headerName: 'Function tree',
                minWidth: 220,
                cellStyle: { 'padding-left': '0px' },
                lockPosition: 'left',
                flex: 1,
                cellRendererParams: {
                  suppressCount: true,
                  innerRenderer: HierarchyLevelCell,
                },
              }}
              defaultColDef={{
                cellStyle: {
                  display: 'flex',
                  alignItems: 'center',
                },
                headerComponent: CustomHeaderCell,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export { CurrentPayDetailsTreeTable };
